import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import '../AiGovernance/AiGovernance.css';
import './isms.css';
import IsmsNav from './IsmsNav';
import { getOverview, errMsg } from './ismsApi';

// Where each checklist step is completed.
const STEP_META = {
  owner: { to: '/dashboard/isms/roles', title: ['Isms.stepOwner', 'Appoint the ISMS owner'] },
  scope: { to: '/dashboard/scope', title: ['Isms.stepScope', 'Define the ISMS scope'] },
  risks: { to: '/dashboard/isms/risks', title: ['Isms.stepRisks', 'Build the risk register'] },
  soa: { to: '/dashboard/isms/soa', title: ['Isms.stepSoa', 'Review the Statement of Applicability'] },
  audit: { to: '/dashboard/compliance/audits', title: ['Isms.stepAudit', 'Run the ISO 27001 audit'] },
};
const ORDER = ['owner', 'scope', 'risks', 'soa', 'audit'];

export default function IsmsOverview() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  useEffect(() => {
    let alive = true;
    getOverview()
      .then((d) => { if (alive) setData(d); })
      .catch((e) => { if (alive) setError(errMsg(e, t('Isms.loadFailed', 'Could not load the ISMS overview.'))); })
      .finally(() => { if (alive) setLoading(false); });
    return () => { alive = false; };
  }, [t]);

  const steps = ORDER.map((k) => (data?.steps || []).find((s) => s.key === k) || { key: k, status: 'TODO', detail: '' });
  const done = steps.filter((s) => s.status === 'DONE').length;
  const c = data?.counts || {};

  return (
    <div className="aig-page">
      <div className="aig-header">
        <h1 className="aig-title">{t('Isms.title', 'ISO 27001 ISMS')}</h1>
        <p className="aig-subtitle">{t('Isms.subtitle', 'Owner, risk register and Statement of Applicability: the artifacts an ISO 27001 auditor reads first.')}</p>
      </div>
      <IsmsNav />

      {loading ? <div className="aig-spinner" /> : error ? <div className="isms-error">{error}</div> : (
        <>
          <div className="aig-stat-bar">
            <div className="aig-stat"><div className="aig-stat-label">{t('Isms.statSteps', 'Steps done')}</div><div className="aig-stat-val">{done}/{ORDER.length}</div></div>
            <div className="aig-stat"><div className="aig-stat-label">{t('Isms.statRisks', 'Risks')}</div><div className="aig-stat-val">{c.risks ?? 0}</div></div>
            <div className="aig-stat"><div className="aig-stat-label">{t('Isms.statAbove', 'Above threshold')}</div><div className={`aig-stat-val ${c.risksAboveThreshold ? 'aig-val-bad' : ''}`}>{c.risksAboveThreshold ?? 0}</div></div>
            <div className="aig-stat"><div className="aig-stat-label">{t('Isms.statApplicable', 'Applicable controls')}</div><div className="aig-stat-val">{c.applicable ?? 0}/{c.controls ?? 0}</div></div>
            <div className="aig-stat"><div className="aig-stat-label">{t('Isms.statExcluded', 'Excluded')}</div><div className="aig-stat-val">{c.excluded ?? 0}</div></div>
          </div>

          <div className="isms-hint">
            {t('Isms.defaultsHint', 'Short on time? Use the recommended defaults: appoint an owner, answer the three profile questions on the Statement of Applicability page and accept the suggestions. You can change any decision later and every exclusion keeps its reason.')}
          </div>

          <div className="isms-steps">
            {steps.map((s, idx) => {
              const meta = STEP_META[s.key];
              return (
                <div key={s.key} className={`isms-step isms-step-${s.status}`}>
                  <div className="isms-step-num">{s.status === 'DONE' ? '✓' : idx + 1}</div>
                  <div className="isms-step-body">
                    <div className="isms-step-title">{t(meta.title[0], meta.title[1])}</div>
                    {s.detail && <div className="isms-step-detail">{s.detail}</div>}
                  </div>
                  <button type="button" className="aig-btn" onClick={() => navigate(meta.to)}>
                    {s.status === 'DONE' ? t('Isms.review', 'Review') : t('Isms.open', 'Open')}
                  </button>
                </div>
              );
            })}
          </div>
        </>
      )}
    </div>
  );
}
